import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ApiService } from 'src/app/services/api.service';
import { AuthGuard, AuthService, Profile } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { EnumToKeyPipe } from 'src/app/services/wording-enum-pipe';
import { CreateUserRequest, GetUserResponse, GetUserResponseLite, UpdateUserRequest } from '../../services/dtos';

type ValidationError = 'emptyField' | 'noRoles';

@Component({
	templateUrl: './user-summary-page.component.html',
	styleUrls: ['./user-summary-page.component.scss'],
	providers: [ConfirmationService, NotificationService, TranslatePipe, EnumToKeyPipe],
})
export class UserSummaryPageComponent implements OnInit {
	errorEmailType: ValidationError | null = null;
	errorRoleType: ValidationError | null = null;

	users: GetUserResponseLite[];
	profile: Profile;

	showNewUserDialog: boolean;

	newUser: CreateUserRequest;

	currentUserDraft: UpdateUserRequest;

	userRoleList: string[];

	constructor(
		private apiService: ApiService,
		private authGuard: AuthGuard,
		private authService: AuthService,
		private router: Router,
		private route: ActivatedRoute,
		private confirmationService: ConfirmationService,
		private notificationService: NotificationService,
		private translatePipe: TranslatePipe,
		private enumToKeyPipe: EnumToKeyPipe,
	) {}

	ngOnInit() {
		this.authService.loadUserProfile().then((profile: Profile) => {
			this.profile = profile;
		});
		this.updateUsers();
	}

	updateUsers() {
		this.apiService.exploitation_getUsers().subscribe((response) => {
			this.users = response.users;
		});
	}

	onSelection(user: GetUserResponse) {
		this.router.navigateByUrl(`inventaire/utilisateur/${user.id}`);
	}

	openCreationUserDialog() {
		this.newUser = { email: '', enabled: true, roles: [] };
		this.showNewUserDialog = true;
		this.errorRoleType = null;
		this.errorEmailType = null;
	}

	hideCreationUserDialog() {
		this.showNewUserDialog = false;
	}

	private validateDraft() {
		const validateEmail = this.validateEmail();
		const validateRoles = this.validateRoles();
		return validateEmail && validateRoles;
	}

	private validateEmail() {
		const hasEmail = this.newUser.email !== '';
		if (!hasEmail) {
			this.errorEmailType = 'emptyField';
			return false;
		}
		return true;
	}

	private validateRoles() {
		const hasRoles = this.newUser.roles.length !== 0;
		if (!hasRoles) {
			this.errorRoleType = 'noRoles';
			return false;
		}
		return true;
	}

	createUser() {
		if (this.validateDraft()) {
			this.showNewUserDialog = false;
			this.apiService.user_postUser(this.newUser).subscribe(
				() => {
					this.notificationService.notifySuccess('Utilisateur créé');
					this.updateUsers();
				},
				(err) => {
					this.notificationService.notifyError(err.statusText);
				},
			);
		}
	}

	updateUser(user: GetUserResponse) {
		this.confirmationService.confirm({
			message: user.enabled
				? "Êtes vous sûr de vouloir desactiver l'utilisateur " + user.email + ' ?'
				: "Êtes vous sûr de vouloir activer l'utilisateur " + user.email + ' ?',
			header: 'Confirmer',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				if (user.enabled) {
					this.currentUserDraft = { email: user.email, enabled: false, roles: user.roles };
				} else {
					this.currentUserDraft = { email: user.email, enabled: true, roles: user.roles };
				}
				this.apiService.user_putUser(this.currentUserDraft, user.id).subscribe(
					() => {
						this.notificationService.notifySuccess(
							user.enabled ? 'Utilisateur désactivé' : 'Utilisateur activé',
						),
							this.updateUsers();
					},
					(err) => {
						this.notificationService.notifyError(err.statusText);
					},
				);
			},
		});
	}
}
